import React, { useState, useEffect } from 'react';
import { Table } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { getPositionMappingsApi, createPositionMappingApi, deletePositionMappingApi } from 'api/services/uniformManagement';
import { getUniformItemsApi } from 'api/services/uniformManagement';
import { useSelector } from 'react-redux';
import DeleteModal from 'components/modelpopup/DeleteModal';
import { itemRender } from 'utils/table';

const PositionMappingList = () => {
  const { position: positions } = useSelector((state) => state.common);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uniformItems, setUniformItems] = useState([]);
  const [selectedUniform, setSelectedUniform] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [rotationOverride, setRotationOverride] = useState('');
  const [addingMapping, setAddingMapping] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [filterUniform, setFilterUniform] = useState(null);
  const [filterPosition, setFilterPosition] = useState(null);

  const positionOptions = (positions || []).map(p => ({ label: p.name, value: p.id }));

  const fetchData = () => {
    setLoading(true);
    const params = {};
    if (filterUniform) params.uniform_item = filterUniform.value;
    if (filterPosition) params.position = filterPosition.value;
    getPositionMappingsApi(params).then(response => {
      setData(response.data?.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const fetchUniformItems = () => {
    getUniformItemsApi({ page_size: 1000 }).then(response => {
      const items = (response.data?.results || [])
        .map(i => ({ label: i.name, value: i.id }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
      setUniformItems(items);
    }).catch(() => {});
  };

  useEffect(() => {
    fetchData();
    fetchUniformItems();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filterUniform, filterPosition]);

  const handleAdd = () => {
    if (!selectedUniform || !selectedPosition) return;
    setAddingMapping(true);
    createPositionMappingApi({
      uniform_item: selectedUniform.value,
      position: selectedPosition.value,
      rotation_cycle_months_override: rotationOverride === '' ? null : Number(rotationOverride),
    }).then(() => {
      toast.success('Mapping created', { position: "top-right", autoClose: 3000, transition: Bounce });
      setSelectedUniform(null);
      setSelectedPosition(null);
      setRotationOverride('');
      setAddingMapping(false);
      fetchData();
    }).catch(e => {
      setAddingMapping(false);
      const data = e.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        data?.non_field_errors?.[0] ||
        (data && typeof data === 'object'
          ? Object.entries(data)
              .filter(([k]) => k !== 'status')
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join(' | ')
          : null) ||
        'Failed to create mapping';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const handleDelete = () => {
    deletePositionMappingApi(deleteId).then(() => {
      toast.success('Mapping deleted', { position: "top-right", autoClose: 3000, transition: Bounce });
      setDeleteModal(false);
      setDeleteId(null);
      fetchData();
    }).catch(() => {
      toast.error('Failed to delete', { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const columns = [
    {
      title: 'Uniform Item',
      dataIndex: 'uniform_item',
      render: (item) => item?.name || '-'
    },
    {
      title: 'Position',
      dataIndex: 'position_name',
    },
    {
      title: 'Rotation (months)',
      render: (_, record) => {
        const eff = record.effective_rotation_cycle_months;
        const ovr = record.rotation_cycle_months_override;
        if (!eff) return <span className="text-muted">—</span>;
        return ovr ? <span>{eff} <small className="text-warning">(override)</small></span> : <span>{eff}</span>;
      }
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Button icon="fa fa-trash" className="btn btn-sm btn-outline-danger"
          onClick={() => { setDeleteId(record.id); setDeleteModal(true); }} />
      )
    }
  ];

  return (
    <div>
      <h4 className="mb-3">Position Mappings</h4>

      <div className="card mb-3">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-3">
              <label className="col-form-label">Uniform Item <span className="text-danger">*</span></label>
              <FilterSelect
                isClearable={true}
                onChange={setSelectedUniform}
                optionLabel="label"
                optionValue="value"
                options={uniformItems}
                isLoading={false}
                value={selectedUniform}
                placeholder="Select Uniform Item"
              />
            </div>
            <div className="col-md-3">
              <label className="col-form-label">Position <span className="text-danger">*</span></label>
              <FilterSelect
                isClearable={true}
                onChange={setSelectedPosition}
                optionLabel="label"
                optionValue="value"
                options={positionOptions}
                isLoading={false}
                value={selectedPosition}
                placeholder="Select Position"
              />
            </div>
            <div className="col-md-3">
              <label className="col-form-label">Rotation Override (months) <small className="text-muted">(optional)</small></label>
              <input
                className="form-control"
                type="number"
                min="1"
                value={rotationOverride}
                onChange={e => setRotationOverride(e.target.value)}
                placeholder="Leave blank to use default"
              />
            </div>
            <div className="col-md-3">
              <Button
                label="Add Mapping"
                icon="fa fa-plus"
                className="btn btn-primary"
                loading={addingMapping}
                disabled={!selectedUniform || !selectedPosition || addingMapping}
                onClick={handleAdd}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-3">
          <FilterSelect
            isClearable={true}
            onChange={setFilterUniform}
            optionLabel="label"
            optionValue="value"
            options={uniformItems}
            isLoading={false}
            value={filterUniform}
            placeholder="Filter by Item"
          />
        </div>
        <div className="col-md-3">
          <FilterSelect
            isClearable={true}
            onChange={setFilterPosition}
            optionLabel="label"
            optionValue="value"
            options={positionOptions}
            isLoading={false}
            value={filterPosition}
            placeholder="Filter by Position"
          />
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey={(record) => record.id}
        loading={loading}
        pagination={{ total: data.length, showSizeChanger: true, itemRender }}
        scroll={{ x: "max-content" }}
      />

      <DeleteModal
        Name="Delete Position Mapping"
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteHandler={handleDelete}
      />
    </div>
  );
};

export default PositionMappingList;
